import { useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { BookingRequest } from '@/api/pending'
import { uploadFile } from '@/api/upload'
import { useAccess } from '@/access/useAccess'
import { ago, firstName, fmtDate, formatBRL } from '@/lib/format'
import { Avatar, Button, Chip, Dropzone, Field, Icon, Modal, Pill, useToast } from '@/ui'
import { approveBooking, reviewBooking } from './api'
import { RejectBookingModal } from './RejectBookingModal'
import {
  missingHint,
  missingSteps,
  PAY_METHOD_LABEL,
  PAY_METHODS,
  phoneDigits,
  STATUS_PILL,
} from './review'
import styles from './BookingReviewModal.module.css'

interface Props {
  booking: BookingRequest
  onClose: () => void
}

function Step({
  n,
  done,
  title,
  subtitle,
  optional,
  children,
}: {
  n: number
  done: boolean
  title: string
  subtitle?: string
  optional?: boolean
  children: ReactNode
}) {
  return (
    <section className={styles.step} aria-label={title}>
      <div className={styles.stepHead}>
        <div className={done ? styles.badgeDone : styles.badge} aria-hidden="true">
          {done ? <Icon name="check" size={15} /> : n}
        </div>
        <div className={styles.stepText}>
          <div className={styles.stepTitle}>
            {title}
            {optional && <span className={styles.optional}>Opcional</span>}
          </div>
          {subtitle && <div className={styles.stepSubtitle}>{subtitle}</div>}
        </div>
      </div>
      {children}
    </section>
  )
}

const Kv = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className={styles.kv}>
    <dt>{label}</dt>
    <dd>{children}</dd>
  </div>
)

/** The app's 3-step review as a desktop modal. Pending bookings are editable; the rest read-only. */
export function BookingReviewModal({ booking: b, onClose }: Props) {
  const { condoId } = useAccess()
  const toast = useToast()
  const queryClient = useQueryClient()
  const editable = b.status === 'pending'
  const r = b.review

  const [details, setDetails] = useState(r?.details ?? false)
  const [contactedAt, setContactedAt] = useState<string | null>(r?.contacted_at ?? null)
  const [note, setNote] = useState(r?.contact_note ?? '')
  const [method, setMethod] = useState<string | null>(r?.payment_method ?? null)
  const [file, setFile] = useState<File | null>(null)
  const [uploadId, setUploadId] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string>()
  const [submitting, setSubmitting] = useState(false)
  const [rejecting, setRejecting] = useState(false)

  const paid = b.fee > 0
  // A proof already on record (earlier review) counts, even though this session has no upload id.
  const hasProof = uploadId !== null || !!r?.proof
  const missing = missingSteps(b.fee, { details, hasProof, method })
  const digits = phoneDigits(b.phone)
  const contactDone = !!contactedAt || note.trim() !== ''

  async function attach(f: File) {
    setUploadError(undefined)
    setUploading(true)
    try {
      const up = await uploadFile({
        file: f,
        resourceType: 'booking_payment_proof',
        condominiumId: condoId,
      })
      setFile(f)
      setUploadId(up.upload_id)
    } catch {
      setUploadError('Não foi possível enviar o comprovante. Tente novamente.')
    } finally {
      setUploading(false)
    }
  }

  async function approve() {
    setSubmitting(true)
    try {
      await reviewBooking(condoId, b.id, {
        details_checked: details,
        contacted: !!contactedAt || note.trim() !== '',
        contact_note: note.trim(),
        payment_method: paid ? method : null,
        proof_upload_id: paid ? uploadId : null,
      })
      await approveBooking(condoId, b.id)
      await queryClient.invalidateQueries({ queryKey: ['bookings', condoId] })
      toast(`${b.area} confirmado para ${firstName(b.resident)}`, { icon: 'check' })
      onClose()
    } catch {
      toast('Não foi possível aprovar a reserva. Tente novamente.', { icon: 'error' })
      setSubmitting(false)
    }
  }

  const pill = STATUS_PILL[b.status]
  const proofOnRecord = r?.proof && uploadId === null ? r.proof : null

  return (
    <>
      <Modal
        title={editable ? 'Revisar reserva' : 'Revisão da reserva'}
        width={620}
        onClose={onClose}
        footer={
          editable ? (
            <div className={styles.footer}>
              {missing.length > 0 && (
                <div className={styles.hint}>
                  <Icon name="info" size={15} />
                  <span>{missingHint(missing)}</span>
                </div>
              )}
              <div className={styles.actions}>
                <Button
                  variant="secondary"
                  onClick={() => setRejecting(true)}
                  disabled={submitting}
                >
                  Recusar
                </Button>
                <Button
                  icon="check"
                  disabled={missing.length > 0 || uploading}
                  loading={submitting}
                  onClick={approve}
                >
                  Aprovar reserva
                </Button>
              </div>
            </div>
          ) : (
            <Button variant="secondary" onClick={onClose}>
              Fechar
            </Button>
          )
        }
      >
        <Step
          n={1}
          done={details}
          title="Conferir os detalhes"
          subtitle="Data, horário, número de convidados e regras do espaço."
        >
          <dl className={styles.kvList}>
            <Kv label="Área">
              {b.area} <Pill tone={pill.tone}>{pill.label}</Pill>
            </Kv>
            <Kv label="Morador">
              {b.resident} · {b.unit}
            </Kv>
            <Kv label="Quando">
              {fmtDate(b.date)} · {b.start_time}–{b.end_time}
            </Kv>
            {b.guests ? <Kv label="Convidados">{b.guests} pessoas</Kv> : null}
            <Kv label="Valor">{paid ? formatBRL(b.fee) : 'Gratuito'}</Kv>
            <Kv label="Solicitado">{ago(b.requested_at)}</Kv>
          </dl>
          {b.note && <blockquote className={styles.note}>“{b.note}”</blockquote>}
          {editable ? (
            <label className={details ? styles.checkOn : styles.check}>
              <input
                type="checkbox"
                checked={details}
                onChange={(e) => setDetails(e.target.checked)}
              />
              <span>Conferi os detalhes</span>
            </label>
          ) : (
            <div className={styles.readonlyLine}>
              {details ? 'Detalhes conferidos' : 'Detalhes não registrados'}
            </div>
          )}
        </Step>

        <Step
          n={2}
          done={contactDone}
          optional
          title="Falar com o morador"
          subtitle="Tire dúvidas ou combine o pagamento antes de aprovar."
        >
          <div className={styles.person}>
            <Avatar name={b.resident} size={36} />
            <div>
              <div className={styles.personName}>{b.resident}</div>
              <div className={styles.personMeta}>
                {b.unit} · {b.phone}
              </div>
            </div>
          </div>
          {editable && (
            <>
              <div className={styles.links}>
                <a
                  className={styles.linkButton}
                  href={`tel:+${digits}`}
                  onClick={() => setContactedAt((c) => c ?? new Date().toISOString())}
                >
                  <Icon name="call" size={18} />
                  Ligar
                </a>
                <a
                  className={styles.linkButton}
                  href={`https://wa.me/${digits}`}
                  target="_blank"
                  rel="noreferrer noopener"
                  onClick={() => setContactedAt((c) => c ?? new Date().toISOString())}
                >
                  <Icon name="chat_bubble" size={18} />
                  WhatsApp
                </a>
              </div>
              <Field
                label="Anotação do contato"
                multiline
                value={note}
                onChange={setNote}
                placeholder="Ex.: confirmou 25 convidados, paga por Pix hoje"
              />
            </>
          )}
          {!editable && (
            <div className={styles.readonlyLine}>
              {note || (contactDone ? 'Contato feito, sem anotação.' : 'Sem contato registrado.')}
            </div>
          )}
        </Step>

        {paid && (
          <Step
            n={3}
            done={hasProof && !!method}
            title={`Comprovar pagamento · ${formatBRL(b.fee)}`}
            subtitle="O pagamento é feito fora do app. Anexe o comprovante enviado pelo morador."
          >
            {editable ? (
              <>
                {proofOnRecord && !file && (
                  <div className={styles.proof}>
                    <Icon name={proofOnRecord.kind === 'pdf' ? 'description' : 'image'} size={20} />
                    <span>
                      {proofOnRecord.name} · {proofOnRecord.size}
                    </span>
                  </div>
                )}
                <Dropzone
                  file={file}
                  onFile={attach}
                  accept="image/*,application/pdf"
                  title="Anexar comprovante"
                  description="Imagem ou PDF"
                  rejectedMessage="Formato não aceito. Envie uma imagem ou um PDF."
                  error={uploadError}
                />
                {uploading && <div className={styles.uploading}>Enviando comprovante…</div>}
              </>
            ) : (
              r?.proof && (
                <div className={styles.proof}>
                  <Icon name={r.proof.kind === 'pdf' ? 'description' : 'image'} size={20} />
                  <div>
                    <div className={styles.personName}>{r.proof.name}</div>
                    <div className={styles.personMeta}>
                      {r.proof.size} · anexado {ago(r.proof.at)}
                    </div>
                  </div>
                </div>
              )
            )}
            <div className={styles.methodGroup} role="group" aria-label="Forma de pagamento">
              <span className={styles.methodLabel}>Forma de pagamento</span>
              <div className={styles.chips}>
                {editable ? (
                  PAY_METHODS.map((m) => (
                    <Chip
                      key={m.code}
                      selected={method === m.code}
                      onClick={() => setMethod(method === m.code ? null : m.code)}
                    >
                      {m.label}
                    </Chip>
                  ))
                ) : (
                  <span className={styles.readonlyLine}>
                    {method ? (PAY_METHOD_LABEL[method] ?? method) : 'Não informada'}
                  </span>
                )}
              </div>
            </div>
          </Step>
        )}

        {b.status === 'rejected' && b.reject_reason && (
          <div className={styles.rejected}>Recusada · {b.reject_reason}</div>
        )}
      </Modal>
      {rejecting && (
        <RejectBookingModal
          booking={b}
          onClose={() => setRejecting(false)}
          onRejected={() => {
            setRejecting(false)
            onClose()
          }}
        />
      )}
    </>
  )
}
